import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { businessInfo } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_SERVICE_LABEL = {
  general: 'General Plumbing',
  roof: 'Roof Plumbing',
  maintenance: 'Maintenance Plumbing',
  skylight: 'Skylight Installation',
};

const EMPTY_QUOTE = { name: '', phone: '', service: '' };

const CallNowBox = () => (
  <div data-testid="call-now-box" className="bg-blue-600 rounded-2xl p-8 md:p-12 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
    <div className="flex items-center justify-center mb-6">
      <div className="bg-white/15 backdrop-blur-sm p-6 rounded-full">
        <Phone className="text-white" size={48} />
      </div>
    </div>
    <h3 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Call Us Now</h3>
    <p className="text-white/90 text-center mb-6 text-lg">
      Speak directly with our team for immediate assistance
    </p>
    <a
      href={`tel:${businessInfo.phoneRaw}`}
      data-testid="cta-phone-link"
      className="block bg-white text-blue-600 text-center py-4 px-6 rounded-lg font-bold text-xl hover:bg-blue-50 transition-all duration-200 shadow-md hover:shadow-lg"
    >
      {businessInfo.phone}
    </a>
    <p className="text-white/80 text-center mt-4 text-sm">Tap to call directly from your phone</p>
  </div>
);

const QUOTE_INPUT_CLS =
  'w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/30 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200';

const useQuoteForm = () => {
  const [quoteData, setQuoteData] = useState(EMPTY_QUOTE);
  const [submitting, setSubmitting] = useState(false);

  // `prev` is the functional setState callback parameter, not a closed-over variable.
  // `setQuoteData` is a stable React setState reference and does not need to be a dep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback((e) => {
    setQuoteData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  // `API`, `EMPTY_QUOTE`, `QUOTE_SERVICE_LABEL` are module-level constants and never change.
  // `setQuoteData`/`setSubmitting` are stable React setState references.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, {
        ...quoteData,
        service: QUOTE_SERVICE_LABEL[quoteData.service] || quoteData.service,
      });
      toast.success("Quote request received! We'll call you soon.");
      setQuoteData(EMPTY_QUOTE);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Something went wrong. Please call us directly.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [quoteData]);

  return { quoteData, submitting, handleChange, handleSubmit };
};

const QuoteServiceSelect = ({ value, onChange }) => (
  <select name="service" value={value} onChange={onChange} required data-testid="quote-service" className={QUOTE_INPUT_CLS}>
    <option value="" className="bg-slate-900">Select Service</option>
    <option value="general" className="bg-slate-900">General Plumbing</option>
    <option value="roof" className="bg-slate-900">Roof Plumbing</option>
    <option value="maintenance" className="bg-slate-900">Maintenance Plumbing</option>
    <option value="skylight" className="bg-slate-900">Skylight Installation</option>
  </select>
);

const QuoteFormHeader = () => (
  <>
    <div className="flex items-center justify-center mb-6">
      <div className="bg-white/15 backdrop-blur-sm p-6 rounded-full">
        <FileText className="text-white" size={48} />
      </div>
    </div>
    <h3 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">Get Free Quote</h3>
    <p className="text-white/90 text-center mb-6">Quick quote — no obligations</p>
  </>
);

const QuoteFormBox = () => {
  const { quoteData, submitting, handleChange, handleSubmit } = useQuoteForm();
  return (
    <div data-testid="quote-box" className="bg-red-600 rounded-2xl p-8 md:p-12 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
      <QuoteFormHeader />
      <form onSubmit={handleSubmit} className="space-y-4">
        <input type="text" name="name" value={quoteData.name} onChange={handleChange} required placeholder="Your Name" data-testid="quote-name" className={QUOTE_INPUT_CLS} />
        <input type="tel" name="phone" value={quoteData.phone} onChange={handleChange} required placeholder="Your Phone" data-testid="quote-phone" className={QUOTE_INPUT_CLS} />
        <QuoteServiceSelect value={quoteData.service} onChange={handleChange} />
        <button
          type="submit"
          disabled={submitting}
          data-testid="quote-submit"
          className="w-full bg-white text-red-600 py-4 rounded-lg font-bold text-lg hover:bg-red-50 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
        >
          {submitting ? 'Sending…' : 'Request Quote'}
        </button>
      </form>
    </div>
  );
};

const CTABoxes = () => (
  <section id="quote" data-testid="cta-section" className="py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-8">
        <CallNowBox />
        <QuoteFormBox />
      </div>
    </div>
  </section>
);

export default CTABoxes;
